import { useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { useLocalStorage } from '../../hooks/useLocalStorage'

export interface NavLinkItem {
  label: string
  href: string
  /** Color emoji icon (matches the prototype's navLinks). */
  icon: string
  badge?: number
}

export interface NavDividerItem {
  divider: true
}

/** A nav entry is either a link or a visual divider between groups. */
export type NavItem = NavLinkItem | NavDividerItem

export interface SidebarProps {
  navItems: NavItem[]
  appName: string
  /** Controlled mobile open state (managed by parent) */
  mobileOpen: boolean
  onMobileClose: () => void
  /** Optional footer pinned to the bottom (e.g. CC user block + "Powered by").
   *  Shown only when the sidebar is expanded. */
  footer?: import('react').ReactNode
  /**
   * Brand logo (full wordmark) shown at the top of the sidebar when expanded.
   * When omitted, falls back to a generic mark + appName text (legacy).
   */
  logoSrc?: string
  /** Wordmark shown in dark mode (falls back to logoSrc). */
  logoDarkSrc?: string
  /** Compact square mark shown when the sidebar is collapsed (icon-only). */
  logoMarkSrc?: string
  /** Whether dark mode is active — selects logoDarkSrc over logoSrc. */
  isDarkMode?: boolean
  /**
   * Brand element rendered INSTEAD of the logo image, when the brand is not an
   * image at all (2026-09-11). The Control Center's JustTextAva wordmark is
   * composed text + the Ava avatar — the same mark the login screen renders —
   * so there is no wordmark file to point `logoSrc` at.
   *
   * ⚠️ This component is SHARED with the admin app, which is Best AI Solutions
   * and whose blue wordmark + "Best AI Solutions" alt text are correct. An
   * opt-in prop is what lets one brand change without touching the other.
   */
  brand?: import('react').ReactNode
}

/**
 * Collapsible sidebar navigation for both admin and control-center apps.
 *
 * Desktop: permanently visible, collapses to icon-only (64px) or full (224px).
 *          Collapsed state persisted to localStorage under "sidebar" key.
 * Mobile:  hidden off-screen, slides in when mobileOpen === true.
 *          Overlay backdrop closes it.
 *
 * Active route is highlighted with a left blue border + active bg color,
 * matching the prototype's `.sidebar-link.active` style.
 */
export function Sidebar({ navItems, appName, mobileOpen, onMobileClose, logoSrc, logoDarkSrc, isDarkMode, footer, brand }: SidebarProps) {
  const [collapsed, setCollapsed] = useLocalStorage<boolean>('sidebar', false)

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  const sidebarWidth = collapsed ? 'w-16' : 'w-56'

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar panel — fixed/overlay on mobile, static (in flex flow) on desktop */}
      <aside
        className={`
          fixed md:static top-0 left-0 h-full z-30 md:z-auto
          flex flex-col flex-shrink-0
          theme-sidebar border-r transition-[width,transform] duration-200
          ${sidebarWidth}
          md:translate-x-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        aria-label="Main navigation"
      >
        {/* Brand logo + collapse/close controls (matches prototype sidebar header) */}
        <div className="flex items-center gap-3 px-3 py-4 border-b theme-border h-16 flex-shrink-0">
          {/* Logo — wordmark when expanded, hidden when collapsed (the ☰ remains
              to expand again, matching the prototype). */}
          {!collapsed && (
            brand ? brand : logoSrc ? (
              <img
                src={isDarkMode ? (logoDarkSrc ?? logoSrc) : logoSrc}
                alt="Best AI Solutions"
                className="h-8 w-auto"
              />
            ) : (
              <span className="text-sm font-semibold theme-text truncate">{appName}</span>
            )
          )}

          {/* Desktop collapse toggle (hamburger) */}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="hidden md:flex items-center justify-center theme-text-secondary hover:theme-text ml-auto"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Mobile close */}
          <button
            onClick={onMobileClose}
            className="md:hidden theme-text-secondary hover:theme-text ml-auto"
            aria-label="Close navigation menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 scrollbar-thin">
          {navItems.map((item, idx) => {
            if ('divider' in item) {
              return (
                <div
                  key={`divider-${idx}`}
                  className="my-2 mx-2 border-t theme-border"
                  role="separator"
                  aria-hidden="true"
                />
              )
            }
            return (
              <NavLink
                key={item.href}
                to={item.href}
                end
                className={({ isActive }) => `
                  sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg
                  text-sm font-medium transition-colors cursor-pointer
                  ${isActive
                    ? 'active theme-text border-l-4 border-blue-500 -ml-px pl-[11px]'
                    : 'theme-text-secondary hover:theme-text'
                  }
                `}
                aria-label={item.label}
                onClick={onMobileClose}
              >
                <span className="text-lg w-5 text-center flex-shrink-0 leading-none" aria-hidden="true">{item.icon}</span>
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* Optional footer (CC user block + "Powered by") — expanded only */}
        {footer && !collapsed && (
          <div className="p-3 border-t theme-border flex-shrink-0">{footer}</div>
        )}
      </aside>

      {/* Spacer removed 2026-05-25: when the sidebar was `fixed`, it took
       *  no space in the flex flow, so a spacer was needed to push the main
       *  content rightward. After changing the sidebar to `md:static` (which
       *  puts it BACK in the flex flow), this spacer became duplicate width
       *  — producing a 224px gap between sidebar and content on desktop.
       *  The sidebar itself now provides the spacing it needs. */}
    </>
  )
}

